/** What a stint's numbers mean, worked out from the laps themselves.
 *
 * The service stores what the logger sent — one row per lap, a time, a list of
 * sector times, whether the driver kept it — and stops there. Everything the
 * Sessions screen prints above the table is derived from that list right here,
 * in the browser, and deliberately so: a best lap, a theoretical lap and a
 * consistency figure are not facts about a database row, they are judgements
 * about which laps count, and a judgement belongs somewhere it can be read,
 * argued with and tested rather than buried in a SQL aggregate.
 *
 * Three of those judgements are encoded here, and they are the ones a driver
 * would make looking at their own stint:
 *
 *  - A lap counts when the driver's logger kept it (`valid`) and the service
 *    holds a positive time for it. That is the same test `refreshTotals` in
 *    `src/routes/sessions.ts` applies to `best_lap_ms`, so the best lap on
 *    this screen and the best lap on the session row cannot disagree.
 *  - The theoretical lap is the best sector times combined, taken over
 *    counting laps only, and it is stated only when every sector of the lap
 *    has a best to contribute. A sector driven on a lap that was thrown away
 *    is not a sector that was driven, and three quarters of a theoretical lap
 *    is not a lap time.
 *  - Consistency is the spread of the laps that were actually racing, so the
 *    session's first lap is left out of it. A lap begun from the pits or from
 *    a standing start is not a slow lap, it is a different kind of lap, and
 *    averaging it in makes a tidy stint read as an erratic one. It still
 *    counts towards the best lap and the sector bests, where it is compared
 *    against the other laps rather than mixed with them.
 *
 * The spread is a population standard deviation — divided by the number of
 * laps, not by one less — because these are the laps that were driven, not a
 * sample drawn from some larger population of laps that might have been. With
 * fewer than two laps left after the first is set aside there is nothing to
 * spread, and the screen is told so rather than being handed a confident
 * `0.000`.
 *
 * "The first lap" is the lowest lap number the session holds, not literally
 * lap 1: a logger that numbers from zero, or a session whose opening lap never
 * reached the service, should still have its out-lap set aside.
 */

import type { DriveSession, LapTiming } from '@/lib/types';

/** How a session's laps are travelling, in the words the list and the summary
 *  card both use. `warn` is not a failure — it is work still in the queue. */
export interface SyncState {
  label: string;
  tone: 'ok' | 'warn' | 'muted';
}

function isNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** A lap the driver kept, timed. Everything else is here for the table only. */
export function counts(lap: LapTiming): boolean {
  return lap.valid && isNumber(lap.lap_time_ms) && lap.lap_time_ms > 0;
}

export function countingLaps(laps: readonly LapTiming[]): LapTiming[] {
  return laps.filter(counts);
}

/** The lowest lap number present — the out-lap, whatever it is numbered. */
export function firstLapNumber(laps: readonly LapTiming[]): number | null {
  let lowest: number | null = null;
  for (const lap of laps) {
    if (lowest === null || lap.lap < lowest) lowest = lap.lap;
  }
  return lowest;
}

/** The quickest lap that counts, or null when none of them do. */
export function bestLap(laps: readonly LapTiming[]): LapTiming | null {
  let best: LapTiming | null = null;
  for (const lap of countingLaps(laps)) {
    if (!best || (lap.lap_time_ms as number) < (best.lap_time_ms as number)) best = lap;
  }
  return best;
}

export function bestLapMs(laps: readonly LapTiming[]): number | null {
  return bestLap(laps)?.lap_time_ms ?? null;
}

/** How many sectors this stint's laps are split into: the widest list any
 *  counting lap carries. A lap that reported fewer simply has no value for
 *  the sectors it is missing, which is different from having a zero. */
export function sectorCount(laps: readonly LapTiming[]): number {
  let widest = 0;
  for (const lap of countingLaps(laps)) {
    if (lap.sector_ms.length > widest) widest = lap.sector_ms.length;
  }
  return widest;
}

/** The quickest time recorded in each sector, across counting laps. `null`
 *  where no counting lap timed that sector. */
export function sectorBests(laps: readonly LapTiming[]): Array<number | null> {
  const width = sectorCount(laps);
  const bests = new Array<number | null>(width).fill(null);
  for (const lap of countingLaps(laps)) {
    for (let i = 0; i < width; i += 1) {
      const value = lap.sector_ms[i];
      if (!isNumber(value) || value <= 0) continue;
      const current = bests[i];
      if (current === null || current === undefined || value < current) bests[i] = value;
    }
  }
  return bests;
}

/** The best sectors combined — the lap the driver has already driven, in
 *  pieces. Null unless every sector has a best to contribute. */
export function theoreticalMs(laps: readonly LapTiming[]): number | null {
  const bests = sectorBests(laps);
  if (bests.length === 0) return null;
  let total = 0;
  for (const best of bests) {
    if (best === null) return null;
    total += best;
  }
  return total;
}

/** The laps the consistency figure is measured over: counting laps, less the
 *  session's first. */
export function consistencyLaps(laps: readonly LapTiming[]): LapTiming[] {
  const first = firstLapNumber(laps);
  return countingLaps(laps).filter((lap) => lap.lap !== first);
}

/** The spread of those laps, in milliseconds. Null below two laps, where
 *  there is no spread to report. */
export function consistencyMs(laps: readonly LapTiming[]): number | null {
  const counting = consistencyLaps(laps);
  if (counting.length < 2) return null;
  const times = counting.map((lap) => lap.lap_time_ms as number);
  const mean = times.reduce((total, ms) => total + ms, 0) / times.length;
  const variance =
    times.reduce((total, ms) => total + (ms - mean) * (ms - mean), 0) / times.length;
  return Math.sqrt(variance);
}

/** A lap's gap to the session's best. Zero for the best lap itself, null for
 *  a lap with no time or a session with no best. */
export function deltaToBestMs(lap: LapTiming, best: number | null): number | null {
  if (!isNumber(best) || !isNumber(lap.lap_time_ms)) return null;
  return lap.lap_time_ms - best;
}

/** Per-sector gaps between one lap and a reference, positive where the lap
 *  was slower. `null` wherever either side is missing that sector, because a
 *  gap against nothing is not zero. */
export function sectorDeltasMs(
  lapSectors: ReadonlyArray<number | null>,
  referenceSectors: ReadonlyArray<number | null>,
  width = Math.max(lapSectors.length, referenceSectors.length),
): Array<number | null> {
  const out = new Array<number | null>(width).fill(null);
  for (let i = 0; i < width; i += 1) {
    const mine = lapSectors[i];
    const theirs = referenceSectors[i];
    if (!isNumber(mine) || !isNumber(theirs)) continue;
    out[i] = mine - theirs;
  }
  return out;
}

/** Whether a sector time is the best this stint recorded in that sector —
 *  what the table paints in the accent. */
export function isSectorBest(
  bests: ReadonlyArray<number | null>,
  index: number,
  value: number | null | undefined,
): boolean {
  const best = bests[index];
  return isNumber(best) && isNumber(value) && value === best;
}

/**
 * How much of a session has reached the service.
 *
 * `held` is the number of lap documents the portal can actually see. When it
 * is known — the session is the open one, so its laps have been fetched — the
 * answer is arithmetic: the session row declares how many laps were driven,
 * and anything the service does not hold is still queued on the logger.
 *
 * When it is not known, which is every other row in the list, the honest
 * signal is whether the drive has ended. The logger patches the totals when a
 * session finishes, so a session with no `ended_at` is one the logger is still
 * writing to; a finished one is reported as synced, and the moment its laps
 * are fetched that claim is replaced by the count.
 */
export function syncState(session: DriveSession, held: number | null): SyncState {
  const arrived = held ?? session.laps_synced ?? null;
  if (arrived !== null) {
    const queued = Math.max(0, session.laps - arrived);
    if (queued > 0) {
      return { label: `${queued} ${queued === 1 ? 'lap' : 'laps'} queued`, tone: 'warn' };
    }
    return { label: `synced ${arrived}/${Math.max(session.laps, arrived)}`, tone: 'ok' };
  }
  if (!session.ended_at) return { label: 'recording', tone: 'warn' };
  return { label: 'synced', tone: 'ok' };
}

/** The colour a sync state is written in. Kept beside the state so the list
 *  and the summary card cannot drift apart. */
export function syncColour(state: SyncState): string {
  switch (state.tone) {
    case 'ok':
      return 'var(--color-ok)';
    case 'warn':
      return 'var(--color-warn)';
    case 'muted':
      return 'var(--color-neutral-600)';
  }
}
